import { useMemo, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart4, Info, Activity } from "lucide-react";
import { CarbonPieChart } from "./CarbonPieChart";
import { MaterialBreakdownChart } from "./MaterialBreakdownChart";
import { AllocationAnalysisCard } from "./AllocationAnalysisCard";
import { TransportLegsCard } from "./TransportLegsCard";
import { formatNumber } from "@/utils/numberFormatter";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useData } from "@/hooks/useData";
import {
  FootprintSummary,
  CarbonChartData,
  FootprintDocument,
  FootprintProduct,
  FootprintConsumption,
  AllocationMethod,
} from "@/types/results";

interface CarbonResultsProps {
  selectedProduct: string;
  selectedYear: string;
  onProductChange: (product: string) => void;
  onYearChange: (year: string) => void;
  isSharedView?: boolean;
  sharedCompanyId?: string;
}

export const CarbonResults = ({
  selectedProduct,
  selectedYear,
  onProductChange,
  onYearChange,
  isSharedView = false,
  sharedCompanyId,
}: CarbonResultsProps) => {
  const { t } = useLanguage();
  const { company, loading: companyLoading } = useCurrentUserCompany();

  const effectiveCompanyId = isSharedView ? sharedCompanyId : company?.id;

  // Fetch data from the new collections
  const { data: footprintDocuments, loading: footprintsLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts, loading: productsLoading } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  const { data: footprintConsumptions, loading: consumptionsLoading } = useData<FootprintConsumption>({
    collectionName: "footprint_consumptions",
  });

  // Filter and organize data for current company
  const companyData = useMemo(() => {
    if (companyLoading && !isSharedView) {
      return { footprints: [], products: [], consumptions: [] };
    }

    if (!effectiveCompanyId) {
      return { footprints: [], products: [], consumptions: [] };
    }

    // Filter footprint documents for the current company
    const companyFootprints = footprintDocuments?.filter((fp) => fp.company_id === effectiveCompanyId) || [];

    // Get all footprint IDs for this company
    const companyFootprintIds = companyFootprints.map((fp) => fp.id);

    // Filter products and consumptions that belong to this company's footprints
    const companyProducts = footprintProducts?.filter((fp) => companyFootprintIds.includes(fp.footprint_id)) || [];
    const companyConsumptions =
      footprintConsumptions?.filter((fc) => companyFootprintIds.includes(fc.footprint_id)) || [];

    return {
      footprints: companyFootprints,
      products: companyProducts,
      consumptions: companyConsumptions,
    };
  }, [effectiveCompanyId, footprintDocuments, footprintProducts, footprintConsumptions, companyLoading, isSharedView]);

  // Create carbon results for the charts and dashboard
  const carbonResults = useMemo(() => {
    if (companyData.footprints.length === 0) {
      return [];
    }

    const results: any[] = [];

    companyData.products.forEach((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      if (footprintDoc) {
        // Get consumption breakdowns for this product's footprint
        const relatedConsumptions = companyData.consumptions.filter((c) => c.footprint_id === product.footprint_id);

        // Calculate phase breakdowns based on consumption data
        const materialsCarbon = relatedConsumptions
          .filter((c) => c.consumption_type === "material")
          .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);

        const electricityCarbon = relatedConsumptions
          .filter((c) => c.consumption_type === "electricity")
          .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);

        const fuelsCarbon = relatedConsumptions
          .filter((c) => c.consumption_type === "fuels")
          .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);

        const residueCarbon = relatedConsumptions
          .filter((c) => c.consumption_type === "residue")
          .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);

        // Create result object for charts and dashboard
        const result = {
          id: product.id,
          year: footprintDoc.year,
          companyId: footprintDoc.company_id,
          nome_produto: product.name || "Unknown Product",
          tipo_produto: product.product_type || "outro",
          uF: product.functional_unit?.quantity || 1,
          U: product.functional_unit?.unit || "m3",
          q: product.annual_production || 0,
          fat_prod: product.product_revenue || 0,
          rd: (() => {
            // Calculate decision ratio properly from PVP values
            const generalData = footprintDoc.general_data;
            const activityType = generalData?.activityType;

            // Determine activity type from product flow if activityType is undefined
            const effectiveActivityType = activityType || product.product_flow || "extraction";

            if (effectiveActivityType === "extraction") {
              const pvpCaro = Number(generalData.pvp_caro_ext) || 0;
              const pvpBarato = Number(generalData.pvp_barato_ext) || 0;
              const ratio = pvpBarato > 0 ? pvpCaro / pvpBarato : 1;

              return ratio;
            } else if (effectiveActivityType === "transformation") {
              const pvpCaro = Number((generalData as any).pvp_caro_trsf) || 0;
              const pvpBarato = Number((generalData as any).pvp_barato_trsf) || 0;
              const ratio = pvpBarato > 0 ? pvpCaro / pvpBarato : 1;

              return ratio;
            }

            return 1; // Default fallback
          })(),
          r_prod: product.ratio_by_quantity || 0,
          r_fat: product.ratio_by_revenue || 0,
          pc_mats: [],
          ph_mats: [],
          createdAt: product.created_at || product.createdAt,
          updatedAt: product.updated_at || product.updatedAt,
          activityType: product.product_flow || footprintDoc.general_data?.activityType || "extraction",
          pc_prod: (product.carbon_footprint_total || 0) * (product.annual_production || 0), // Annual total instead of per-unit

          // Use actual calculated values from the product instead of arbitrary multipliers
          // For extraction activities, use the actual breakdown values (converted to annual totals)
          pc_ext_materials:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? (product.carbon_footprint_materials || materialsCarbon) * (product.annual_production || 0)
              : undefined,
          pc_ext_ecr:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon) *
                (product.annual_production || 0)
              : undefined,
          pc_ext:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? ((product.carbon_footprint_materials || materialsCarbon) +
                  (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon)) *
                (product.annual_production || 0)
              : product.carbon_footprint_extraction !== undefined
                ? product.carbon_footprint_extraction * (product.annual_production || 0)
                : undefined,

          // For transformation activities, use backend calculated values when available (converted to annual totals)
          pc_trans:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
            ? (product.carbon_footprint_transport || 0) * (product.annual_production || 0)
            : undefined,

          pc_trsf_materials:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? (product.carbon_footprint_materials || materialsCarbon) * (product.annual_production || 0)
              : undefined,
          pc_trsf_ecr:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon) *
                (product.annual_production || 0)
              : undefined,
          pc_trsf:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? ((product.carbon_footprint_materials || materialsCarbon) +
                  (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon)) *
                (product.annual_production || 0)
              : undefined,

          calculatedAt: footprintDoc.calculation_metadata?.last_calculated || footprintDoc.createdAt,
        };

        results.push(result);
      }
    });

    return results;
  }, [companyData]);

  // Create footprint summaries for the data table
  const footprintSummaries: FootprintSummary[] = useMemo(() => {
    if (companyData.footprints.length === 0) return [];

    const summaries: FootprintSummary[] = [];

    companyData.products.forEach((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      if (footprintDoc && product.functional_unit) {
        // Determine allocation method for carbon footprint
        const generalData = footprintDoc.general_data;
        let carbonAllocationMethod: AllocationMethod = "economic"; // Default
        if (generalData) {
          const activityType = generalData.activityType;
          if (activityType === "extraction") {
            const pvpCaro = generalData.pvp_caro_ext || 0;
            const pvpBarato = generalData.pvp_barato_ext || 0;
            const rd = pvpBarato > 0 ? pvpCaro / pvpBarato : Infinity;
            carbonAllocationMethod = rd <= 5 ? "physical" : "economic";
          } else if (activityType === "transformation") {
            // Use optional chaining for transformation properties that might not exist in the type
            const pvpCaro = (generalData as any).pvp_caro_trsf || 0;
            const pvpBarato = (generalData as any).pvp_barato_trsf || 0;
            const rd = pvpBarato > 0 ? pvpCaro / pvpBarato : Infinity;
            carbonAllocationMethod = rd <= 5 ? "physical" : "economic";
          }
        }

        summaries.push({
          id: product.id,
          product_name: product.name || "Unknown Product",
          product_type: product.product_type || "outro",
          stone_type_name: product.stone_type_name || "Não especificado",
          year: footprintDoc.year,
          functional_unit: product.functional_unit.quantity || 1,
          unit: product.functional_unit.unit || "m3",
          annual_production: product.annual_production || 0, // Added for linter compliance
          carbon_footprint: product.carbon_footprint_total || 0,
          hydric_footprint: product.hydric_footprint_total || 0,
          activity_type: footprintDoc.general_data?.activityType || "extraction",
          allocation_method: carbonAllocationMethod, // Calculated allocation method for carbon
          companyId: footprintDoc.company_id,
        });
      }
    });

    return summaries;
  }, [companyData]);

  // Get unique values for filters
  const products = useMemo(() => {
    return Array.from(new Set(footprintSummaries.map((p) => p.product_name)));
  }, [footprintSummaries]);

  const years = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }
    return Array.from(
      new Set(footprintSummaries.filter((fp) => fp.product_name === selectedProduct).map((fp) => fp.year.toString()))
    ).sort((a, b) => parseInt(b) - parseInt(a));
  }, [footprintSummaries, selectedProduct]);

  useEffect(() => {
    if (selectedProduct && selectedYear && years.length > 0) {
      const yearExists = years.includes(selectedYear);
      if (!yearExists) {
        onYearChange("");
      }
    }
  }, [selectedProduct, selectedYear, years, onYearChange]);

  // Get selected product details for dashboard
  const selectedProductData = useMemo(() => {
    if (!selectedProduct || !selectedYear) return null;

    return carbonResults.find(
      (result) => result.nome_produto === selectedProduct && result.year.toString() === selectedYear
    );
  }, [selectedProduct, selectedYear, carbonResults]);

  // Get the actual FootprintProduct for the selected product
  const selectedFootprintProduct = useMemo(() => {
    if (!selectedProduct || !selectedYear) return null;

    return companyData.products.find((p) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === p.footprint_id);
      return p.name === selectedProduct && footprintDoc?.year.toString() === selectedYear;
    });
  }, [selectedProduct, selectedYear, companyData]);

  // Generate chart data based on activity type
  const chartData = useMemo((): CarbonChartData[] => {
    if (!selectedProductData) return [];

    const { activityType } = selectedProductData;
    const data: CarbonChartData[] = [];

    if (activityType === "extraction") {
      // RF 11.1 - Extraction only
      // Calculate the total of components being displayed for percentage calculation
      const totalDisplayedComponents =
        (selectedProductData.pc_ext_materials || 0) + (selectedProductData.pc_ext_ecr || 0);

      // Only show components if they have meaningful values and total is positive
      if (totalDisplayedComponents > 0) {
        if (selectedProductData.pc_ext_materials && selectedProductData.pc_ext_materials > 0) {
          data.push({
            name: t("results.carbon.extraction.materials"),
            value: selectedProductData.pc_ext_materials,
            percentage: (selectedProductData.pc_ext_materials / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
          });
        }
        if (selectedProductData.pc_ext_ecr && selectedProductData.pc_ext_ecr > 0) {
          data.push({
            name: t("results.carbon.extraction.ecr"),
            value: selectedProductData.pc_ext_ecr,
            percentage: (selectedProductData.pc_ext_ecr / totalDisplayedComponents) * 100,
            color: "#0891b2", // cyan-600
          });
        }
      }
    } else if (activityType === "transformation") {
      // RF 11.2 - Transformation
      const totalDisplayedComponents =
        (selectedProductData.pc_ext || 0) + (selectedProductData.pc_trans || 0) + (selectedProductData.pc_trsf || 0);

      if (totalDisplayedComponents > 0) {
        if (selectedProductData.pc_ext && selectedProductData.pc_ext > 0) {
          data.push({
            name: t("results.carbon.phases.extraction"),
            value: selectedProductData.pc_ext,
            percentage: (selectedProductData.pc_ext / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
          });
        }
        // Only show transport for transformation products, not extraction
        const productFlow = selectedFootprintProduct?.product_flow || "transformation";
        if (selectedProductData.pc_trans && selectedProductData.pc_trans > 0 && productFlow !== "extraction") {
          data.push({
            name: t("results.carbon.phases.transportation"),
            value: selectedProductData.pc_trans,
            percentage: (selectedProductData.pc_trans / totalDisplayedComponents) * 100,
            color: "#f59e0b", // amber-500
          });
        }
        if (selectedProductData.pc_trsf && selectedProductData.pc_trsf > 0) {
          data.push({
            name: t("results.carbon.phases.transformation"),
            value: selectedProductData.pc_trsf,
            percentage: (selectedProductData.pc_trsf / totalDisplayedComponents) * 100,
            color: "#8b5cf6", // violet-500
            breakdown: {
              materials: selectedProductData.pc_trsf_materials,
              ecr: selectedProductData.pc_trsf_ecr,
            },
          });
        }
      }
    } else if (activityType === "extraction_and_transformation") {
      // RF 11.3 - Extraction and Transformation
      const totalDisplayedComponents =
        (selectedProductData.pc_ext || 0) + (selectedProductData.pc_trans || 0) + (selectedProductData.pc_trsf || 0);

      if (totalDisplayedComponents > 0) {
        if (selectedProductData.pc_ext && selectedProductData.pc_ext > 0) {
          data.push({
            name: t("results.carbon.phases.extraction"),
            value: selectedProductData.pc_ext,
            percentage: (selectedProductData.pc_ext / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
            breakdown: {
              materials: selectedProductData.pc_ext_materials,
              ecr: selectedProductData.pc_ext_ecr,
            },
          });
        }
        // Only show transport for transformation products, not extraction
        const productFlow = selectedFootprintProduct?.product_flow || "transformation";
        if (selectedProductData.pc_trans && selectedProductData.pc_trans > 0 && productFlow !== "extraction") {
          data.push({
            name: t("results.carbon.phases.transportation"),
            value: selectedProductData.pc_trans,
            percentage: (selectedProductData.pc_trans / totalDisplayedComponents) * 100,
            color: "#f59e0b", // amber-500
          });
        }
        if (selectedProductData.pc_trsf && selectedProductData.pc_trsf > 0) {
          data.push({
            name: t("results.carbon.phases.transformation"),
            value: selectedProductData.pc_trsf,
            percentage: (selectedProductData.pc_trsf / totalDisplayedComponents) * 100,
            color: "#8b5cf6", // violet-500
            breakdown: {
              materials: selectedProductData.pc_trsf_materials,
              ecr: selectedProductData.pc_trsf_ecr,
            },
          });
        }
      }
    }

    return data;
  }, [selectedProductData, t]);

  // Get legend content based on activity type
  const getLegendContent = () => {
    if (!selectedProductData) return null;

    const { activityType } = selectedProductData;
    const productFlow = selectedFootprintProduct?.product_flow;

    // When productFlow is available, use it as the primary determinant
    // This handles mixed companies where activityType might not be "extraction_and_transformation"
    const isExtractionProduct = productFlow === "extraction" ||
      (!productFlow && activityType === "extraction");

    const isTransformationProduct = productFlow === "transformation" ||
      (!productFlow && activityType === "transformation");

    if (isExtractionProduct) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-700 font-medium">{t("results.carbon.legend.extraction.description")}</p>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• {t("results.carbon.legend.extraction.materials")}</p>
            <p>• {t("results.carbon.legend.extraction.energyResidue")}</p>
          </div>
        </div>
      );
    }

    if (isTransformationProduct) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-700 font-medium">{t("results.carbon.legend.transformation.description")}</p>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• {t("results.carbon.legend.transformation.extraction")}</p>
            <p>• {t("results.carbon.legend.transformation.transport")}</p>
            <p>• {t("results.carbon.legend.transformation.transformation")}</p>
          </div>
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-gray-600">
              <strong>Nota:</strong> {t("results.carbon.legend.transformation.materials")}
            </p>
          </div>
        </div>
      );
    }

    // Fallback case - should not normally happen but default to extraction
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-700 font-medium">{t("results.carbon.legend.extraction.description")}</p>
        <div className="space-y-2 text-sm text-gray-600">
          <p>• {t("results.carbon.legend.extraction.materials")}</p>
          <p>• {t("results.carbon.legend.extraction.energyResidue")}</p>
        </div>
      </div>
    );
  };

  // Loading state
  const loading = footprintsLoading || productsLoading || consumptionsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Empty state when no company is found (only for non-shared views)
  if (!company && !isSharedView) {
    return (
      <div className="text-center py-16">
        <Activity className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t("results.empty.noCompany.title")}</h3>
        <p className="text-gray-500 max-w-md mx-auto">{t("results.empty.noCompany.description")}</p>
      </div>
    );
  }

  // Empty state when no footprint data exists for the company
  if (companyData.footprints.length === 0 && companyData.products.length === 0) {
    return (
      <div className="space-y-6">
        {/* Empty State */}
        <Card>
          <CardContent className="text-center py-16">
            <BarChart4 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("results.carbon.empty.title")}</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">{t("results.carbon.empty.description")}</p>
            <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto">
              <h4 className="font-medium text-gray-900 mb-2">{t("results.carbon.empty.getStarted")}</h4>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li>• {t("results.carbon.empty.step1")}</li>
                <li>• {t("results.carbon.empty.step2")}</li>
                <li>• {t("results.carbon.empty.step3")}</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart and Analysis Section */}
      {carbonResults.length > 0 ? (
        <>
          {!isSharedView && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("results.carbon.filters.product")}</label>
                <Select value={selectedProduct} onValueChange={onProductChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("results.carbon.filters.selectProduct")} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product} value={product}>
                        {product}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("results.carbon.filters.year")}</label>
                <Select value={selectedYear} onValueChange={onYearChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("results.carbon.filters.selectYear")} />
                  </SelectTrigger>
                  <SelectContent>
                    {years.length > 0 ? (
                      years.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-gray-500">Nenhum ano disponível para este produto</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Chart Analysis Dashboard */}
          {selectedProduct && selectedYear ? (
            selectedProductData ? (
              <div className="space-y-6">
                {/* Total Footprint */}
                <Card data-pdf-section="carbon-total" className="bg-teal-50 border-teal-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <BarChart4 className="h-8 w-8 text-teal-600" />
                      <div>
                        <h3 className="text-lg font-semibold text-teal-800">
                          {t("results.carbon.dashboard.totalFootprint")}
                        </h3>
                        <p className="text-3xl font-bold text-teal-700">
                          {formatNumber(selectedProductData.pc_prod)} {t("results.carbon.units.kgCO2e")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Chart and Legend */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div data-pdf-section="carbon-pie-chart">
                    <CarbonPieChart data={chartData} title={t("results.carbon.dashboard.chart")} />
                  </div>

                  <Card data-pdf-section="carbon-legend">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        {t("results.carbon.legend.title")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>{getLegendContent()}</CardContent>
                  </Card>
                </div>

                {/* Enhanced Analysis Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Material Breakdown Chart */}
                  {selectedFootprintProduct && (
                    <div data-pdf-section="carbon-material-breakdown">
                      <MaterialBreakdownChart
                        product={selectedFootprintProduct}
                        consumptions={companyData.consumptions}
                        type="carbon"
                      />
                    </div>
                  )}

                  {/* Allocation Analysis */}
                  <div data-pdf-section="carbon-allocation">
                    <AllocationAnalysisCard data={selectedProductData} />
                  </div>
                </div>

                {/* Transport legs breakdown (transformation products) */}
                {selectedFootprintProduct?.transports && selectedFootprintProduct.transports.length > 0 && (
                  <TransportLegsCard transports={selectedFootprintProduct.transports} />
                )}
              </div>
            ) : (
              // Empty state for selected product/year with no data
              <div className="text-center py-16">
                <BarChart4 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Sem Dados de Pegada de Carbono</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-4">
                  Não foram encontrados dados de pegada de carbono para <strong>{selectedProduct}</strong> no ano{" "}
                  <strong>{selectedYear}</strong>.
                </p>
                <p className="text-sm text-gray-400">
                  Verifique se os cálculos de pegada foram realizados para esta combinação de produto e ano.
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-12 text-gray-500">
              <BarChart4 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {t("results.carbon.filters.selectProduct")} {t("common.and").toLowerCase()}{" "}
                {t("results.carbon.filters.selectYear").toLowerCase()}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <BarChart4 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">{t("results.carbon.dashboard.empty.title")}</h3>
          <p className="text-gray-500">{t("results.carbon.dashboard.empty.description")}</p>
        </div>
      )}
    </div>
  );
};
